import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, type Participant } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import HomeContent from "../components/HomeContent";

interface Room {
  id: number; name: string; rentCents: number; rentFrequency: string;
  ensuite: boolean; occupant: { participantId: number; name: string } | null;
}
interface HomeDetail {
  id: number; name: string; address: string; sdaType: string;
  bathrooms: number; parking: number; status: string; rooms: Room[];
}
interface Maintenance {
  id: number; description: string; priority: string; status: string; costCents: number | null; roomId: number | null;
}

const field = "input mt-0 text-sm";
const priorityChip: Record<string, string> = {
  low: "bg-slate-100 text-body", medium: "bg-sky-50 text-sky-700",
  high: "bg-amber-50 text-amber-700", urgent: "bg-red-100 text-red-700",
};
const money = (c: number) => `$${(c / 100).toFixed(2)}`;

export default function HomeDetail() {
  const { id } = useParams();
  const home = useFetch<HomeDetail>(`/v1/sil/homes/${id}`);
  const participants = useFetch<Participant[]>("/v1/participants");
  const maintenance = useFetch<Maintenance[]>(`/v1/sil/homes/${id}/maintenance`);
  const [roomName, setRoomName] = useState("");

  async function addRoom(e: React.FormEvent) {
    e.preventDefault();
    if (!roomName.trim()) return;
    await api(`/v1/sil/homes/${id}/rooms`, { method: "POST", body: JSON.stringify({ name: roomName.trim() }) });
    setRoomName(""); home.reload();
  }
  async function moveOut(roomId: number) {
    await api(`/v1/sil/rooms/${roomId}/move-out`, {
      method: "POST", body: JSON.stringify({ moveOutDate: new Date().toISOString().slice(0, 10) }),
    });
    home.reload();
  }

  if (home.loading) return <div className="skeleton h-64" />;
  if (home.error || !home.data) return <p className="text-sm text-red-600">{home.error ?? "Not found"}</p>;
  const h = home.data;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/homes" className="text-sm text-teal-700 hover:underline">← Homes</Link>
        <h1 className="text-2xl font-semibold text-ink mt-1">{h.name}</h1>
        <p className="text-sm text-mut">{h.address} · <span className="capitalize">{h.sdaType.replace(/_/g, " ")}</span> · {h.bathrooms} bath · {h.parking} parking</p>
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-ink">Rooms</h2>
        <div className="space-y-2">
          {h.rooms.map((r) => (
            <div key={r.id} className="card p-4 flex flex-wrap items-center gap-3 justify-between">
              <div>
                <p className="font-medium text-ink">{r.name} {r.ensuite && <span className="text-xs text-faint">ensuite</span>}</p>
                <p className="text-xs text-mut">{money(r.rentCents)} / {r.rentFrequency}</p>
              </div>
              {r.occupant ? (
                <div className="flex items-center gap-3">
                  <span className="chip bg-teal-50 text-teal-700">{r.occupant.name}</span>
                  <button onClick={() => moveOut(r.id)} className="text-sm text-red-600 hover:underline">Move out</button>
                </div>
              ) : (
                <AssignForm roomId={r.id} participants={participants.data ?? []} onDone={home.reload} />
              )}
            </div>
          ))}
          {h.rooms.length === 0 && <p className="text-sm text-mut">No rooms yet.</p>}
        </div>
        <form onSubmit={addRoom} className="flex items-center gap-2">
          <input value={roomName} onChange={(e) => setRoomName(e.target.value)} placeholder="New room name" className={`${field} w-56`} />
          <button className="btn-secondary px-3 py-1.5 text-sm">Add room</button>
        </form>
      </section>

      <MaintenanceSection homeId={h.id} rooms={h.rooms} fetch={maintenance} />
      <HomeContent homeId={h.id} />
    </div>
  );
}

function AssignForm({ roomId, participants, onDone }: { roomId: number; participants: Participant[]; onDone: () => void }) {
  const [participantId, setParticipantId] = useState("");
  const [moveInDate, setMoveInDate] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  async function assign() {
    if (!participantId) return;
    setError(null);
    try {
      await api(`/v1/sil/rooms/${roomId}/occupancy`, {
        method: "POST", body: JSON.stringify({ participantId: Number(participantId), moveInDate }),
      });
      onDone();
    } catch (err) { setError((err as Error).message); }
  }

  return (
    <div className="flex items-center gap-2">
      <select value={participantId} onChange={(e) => setParticipantId(e.target.value)} className={`${field} w-44`}>
        <option value="">Assign participant…</option>
        {participants.map((p) => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
      </select>
      <input type="date" value={moveInDate} onChange={(e) => setMoveInDate(e.target.value)} className={`${field} w-40`} />
      <button onClick={assign} className="btn-dark text-sm">Move in</button>
      {error && <span className="text-sm text-red-600">{error}</span>}
    </div>
  );
}

function MaintenanceSection({ homeId, rooms, fetch }: {
  homeId: number; rooms: Room[]; fetch: ReturnType<typeof useFetch<Maintenance[]>>;
}) {
  const [form, setForm] = useState({ description: "", priority: "medium", roomId: "" });

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!form.description.trim()) return;
    await api(`/v1/sil/homes/${homeId}/maintenance`, { method: "POST", body: JSON.stringify({
      description: form.description.trim(), priority: form.priority,
      roomId: form.roomId ? Number(form.roomId) : undefined,
    }) });
    setForm({ description: "", priority: "medium", roomId: "" }); fetch.reload();
  }
  async function setStatus(id: number, status: string, costCents?: number) {
    await api(`/v1/sil/maintenance/${id}/status`, { method: "POST", body: JSON.stringify({ status, costCents }) });
    fetch.reload();
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-ink">Maintenance</h2>
      <form onSubmit={create} className="card p-4 flex flex-wrap items-end gap-2">
        <label className="block flex-1 min-w-48"><span className="label">Description</span>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={field} /></label>
        <label className="block"><span className="label">Priority</span>
          <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={field}>
            {["low", "medium", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select></label>
        <label className="block"><span className="label">Room</span>
          <select value={form.roomId} onChange={(e) => setForm({ ...form, roomId: e.target.value })} className={field}>
            <option value="">Whole home</option>
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select></label>
        <button className="btn-dark text-sm">Log request</button>
      </form>
      <div className="space-y-2">
        {(fetch.data ?? []).map((m) => (
          <div key={m.id} className="card p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-ink">{m.description}</p>
              <p className="text-xs text-mut">
                <span className={`chip ${priorityChip[m.priority]}`}>{m.priority}</span>
                {m.costCents != null && <span className="ml-2">{money(m.costCents)}</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="chip bg-slate-100 text-body capitalize">{m.status.replace(/_/g, " ")}</span>
              {m.status !== "completed" && m.status !== "cancelled" && (
                <>
                  <button onClick={() => setStatus(m.id, "in_progress")} className="text-sm text-sky-700 hover:underline">Start</button>
                  <button onClick={() => setStatus(m.id, "completed")} className="text-sm text-teal-700 hover:underline">Complete</button>
                </>
              )}
            </div>
          </div>
        ))}
        {(fetch.data ?? []).length === 0 && <p className="text-sm text-mut">No maintenance requests.</p>}
      </div>
    </section>
  );
}
